/**
 * readingGoals.ts — Reading goals, streaks, and progress analytics
 *
 * localStorage-backed per-user reading goal tracking.
 * Builds on existing read-tracking (lithub_read_articles) without replacing it.
 *
 * Storage keys (per userId):
 *   lithub_reading_goal_{userId}  — { target, period }
 *   lithub_read_log_{userId}      — [{ pmid, readAt }]
 *
 * Does NOT modify the existing lithub_read_articles set.
 */

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type GoalPeriod = 'week' | 'month';

export interface ReadingGoal {
  target: number;
  period: GoalPeriod;
}

export interface ReadLogEntry {
  pmid: string;
  readAt: string; // ISO datetime
}

export interface StreakInfo {
  currentStreak: number;   // consecutive periods meeting goal
  longestStreak: number;
  lastCompletedPeriod: string | null; // ISO date of last completed period end
}

export interface ProgressAnalytics {
  goal: ReadingGoal | null;
  periodReads: number;          // reads in current period
  periodStart: string;          // ISO date
  periodEnd: string;            // ISO date
  percentComplete: number;      // 0-100
  streak: StreakInfo;
  recentWeeks: WeekSummary[];   // last 8 weeks for sparkline
  totalAllTime: number;
}

export interface WeekSummary {
  weekStart: string; // ISO date (Monday)
  count: number;
}

// ---------------------------------------------------------------------------
// localStorage helpers
// ---------------------------------------------------------------------------

function goalKey(userId: string): string {
  return `lithub_reading_goal_${userId}`;
}

function logKey(userId: string): string {
  return `lithub_read_log_${userId}`;
}

function streakKey(userId: string): string {
  return `lithub_reading_streak_${userId}`;
}

// ---------------------------------------------------------------------------
// Goal CRUD
// ---------------------------------------------------------------------------

export function getGoal(userId: string): ReadingGoal | null {
  try {
    const raw = localStorage.getItem(goalKey(userId));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || !parsed.target || !parsed.period) { localStorage.removeItem(goalKey(userId)); return null; }
    return parsed;
  } catch { localStorage.removeItem(goalKey(userId)); return null; }
}

export function setGoal(userId: string, goal: ReadingGoal): void {
  localStorage.setItem(goalKey(userId), JSON.stringify(goal));
}

export function clearGoal(userId: string): void {
  localStorage.removeItem(goalKey(userId));
}

// ---------------------------------------------------------------------------
// Read log
// ---------------------------------------------------------------------------

export function getReadLog(userId: string): ReadLogEntry[] {
  try {
    const raw = localStorage.getItem(logKey(userId));
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) { localStorage.removeItem(logKey(userId)); return []; }
    return parsed;
  } catch { localStorage.removeItem(logKey(userId)); return []; }
}

export function appendReadLog(userId: string, pmid: string): void {
  try {
    const log = getReadLog(userId);
    // A PMID appears at most once in the log — opening abstract + AI summary
    // on the same article counts once; reopening later does not increment.
    if (log.some(e => e.pmid === pmid)) return;
    log.push({ pmid, readAt: new Date().toISOString() });
    // Cap at 1000 entries, prune oldest
    const capped = log.slice(-1000);
    localStorage.setItem(logKey(userId), JSON.stringify(capped));
  } catch { /* fail silently — don't break LitHub */ }
}

// ---------------------------------------------------------------------------
// Period calculations
// ---------------------------------------------------------------------------

function getWeekStart(date: Date): Date {
  const d = new Date(date);
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1); // Monday
  d.setDate(diff);
  d.setHours(0, 0, 0, 0);
  return d;
}

function getMonthStart(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), 1);
}

function getPeriodBounds(period: GoalPeriod, now: Date = new Date()): { start: Date; end: Date } {
  if (period === 'week') {
    const start = getWeekStart(now);
    const end = new Date(start);
    end.setDate(end.getDate() + 7);
    return { start, end };
  } else {
    const start = getMonthStart(now);
    const end = new Date(start.getFullYear(), start.getMonth() + 1, 1);
    return { start, end };
  }
}

function countReadsInRange(log: ReadLogEntry[], start: Date, end: Date): number {
  // Count unique PMIDs with readAt in [start, end) using local time comparison
  const startMs = start.getTime();
  const endMs = end.getTime();
  const seen = new Set<string>();
  for (const entry of log) {
    const entryMs = new Date(entry.readAt).getTime();
    if (entryMs >= startMs && entryMs < endMs && !seen.has(entry.pmid)) {
      seen.add(entry.pmid);
    }
  }
  return seen.size;
}

// ---------------------------------------------------------------------------
// Streak calculation
// ---------------------------------------------------------------------------

function getStoredStreak(userId: string): StreakInfo {
  try {
    const raw = localStorage.getItem(streakKey(userId));
    return raw ? JSON.parse(raw) : { currentStreak: 0, longestStreak: 0, lastCompletedPeriod: null };
  } catch { return { currentStreak: 0, longestStreak: 0, lastCompletedPeriod: null }; }
}

function saveStreak(userId: string, streak: StreakInfo): void {
  localStorage.setItem(streakKey(userId), JSON.stringify(streak));
}

export function computeStreak(userId: string, goal: ReadingGoal, log: ReadLogEntry[]): StreakInfo {
  const stored = getStoredStreak(userId);
  const now = new Date();
  const { start: currentStart } = getPeriodBounds(goal.period, now);

  // Check previous periods going backwards
  let streak = 0;
  let checkDate = new Date(currentStart);

  // Step back one period at a time
  for (let i = 0; i < 52; i++) { // max 52 periods back
    if (goal.period === 'week') {
      checkDate = new Date(checkDate.getTime() - 7 * 24 * 60 * 60 * 1000);
    } else {
      checkDate = new Date(checkDate.getFullYear(), checkDate.getMonth() - 1, 1);
    }
    const bounds = getPeriodBounds(goal.period, checkDate);
    const count = countReadsInRange(log, bounds.start, bounds.end);
    if (count >= goal.target) {
      streak++;
    } else {
      break;
    }
  }

  // Check if current period is already met (adds to streak display)
  const currentBounds = getPeriodBounds(goal.period, now);
  const currentCount = countReadsInRange(log, currentBounds.start, currentBounds.end);
  const currentMet = currentCount >= goal.target;

  const result: StreakInfo = {
    currentStreak: currentMet ? streak + 1 : streak,
    longestStreak: Math.max(stored.longestStreak, currentMet ? streak + 1 : streak),
    lastCompletedPeriod: currentMet ? currentStart.toISOString() : stored.lastCompletedPeriod,
  };

  saveStreak(userId, result);
  return result;
}

// ---------------------------------------------------------------------------
// Weekly summaries (for sparkline)
// ---------------------------------------------------------------------------

function getRecentWeeks(log: ReadLogEntry[], count: number = 8): WeekSummary[] {
  const now = new Date();
  const weeks: WeekSummary[] = [];

  for (let i = count - 1; i >= 0; i--) {
    const weekDate = new Date(now.getTime() - i * 7 * 24 * 60 * 60 * 1000);
    const start = getWeekStart(weekDate);
    const end = new Date(start.getTime() + 7 * 24 * 60 * 60 * 1000);
    weeks.push({
      weekStart: start.toISOString().slice(0, 10),
      count: countReadsInRange(log, start, end),
    });
  }

  return weeks;
}

// ---------------------------------------------------------------------------
// Full analytics computation
// ---------------------------------------------------------------------------

export function computeAnalytics(userId: string): ProgressAnalytics {
  const goal = getGoal(userId);
  const log = getReadLog(userId);

  if (!goal) {
    return {
      goal: null,
      periodReads: 0,
      periodStart: '',
      periodEnd: '',
      percentComplete: 0,
      streak: { currentStreak: 0, longestStreak: 0, lastCompletedPeriod: null },
      recentWeeks: getRecentWeeks(log),
      totalAllTime: log.length,
    };
  }

  const now = new Date();
  const { start, end } = getPeriodBounds(goal.period, now);
  const periodReads = countReadsInRange(log, start, end);
  const streak = computeStreak(userId, goal, log);

  return {
    goal,
    periodReads,
    periodStart: start.toISOString(),
    periodEnd: end.toISOString(),
    percentComplete: Math.min(Math.round((periodReads / goal.target) * 100), 100),
    streak,
    recentWeeks: getRecentWeeks(log),
    totalAllTime: log.length,
  };
}
